import { space, themeTokens } from "@octopusdeploy/design-system-tokens";
import { logger } from "@octopusdeploy/logging";
import type { LibraryVariableSetInfo, ProjectResource, WorkerPoolResource, ActionTemplateParameterResource, LibraryVariableSetResource, EnvironmentResource, GetScopedCommonTemplatesWithValuesBffResponse, ScopedCommonTemplateTenantValue, VariableType, } from "@octopusdeploy/octopus-server-client";
import { VariableSetContentType, Permission } from "@octopusdeploy/octopus-server-client";
import * as React from "react";
import { useState } from "react";
import CommonTemplateValueInlineCell from "~/areas/projects/components/Variables/TenantVariables/CommonTemplateValueInlineCell";
import CommonTemplatesSidebarContent from "~/areas/projects/components/Variables/TenantVariables/CommonTemplatesSidebarContent";
import { ProjectTenantVariableTemplatesFiltersBar } from "~/areas/projects/components/Variables/TenantVariables/FiltersBar";
import type { ProjectTenantVariablesFilterState } from "~/areas/projects/components/Variables/TenantVariables/FiltersBar";
import { MissingValuesCallout } from "~/areas/projects/components/Variables/TenantVariables/MissingValuesCallout";
import NoCommonTemplateValuesOnboarding from "~/areas/projects/components/Variables/TenantVariables/NoCommonTemplateValuesOnboarding";
import NoVariableSetsOnboarding from "~/areas/projects/components/Variables/TenantVariables/NoVariableSetsOnboarding";
import ScopedCommonTemplateScopeInlineCell from "~/areas/projects/components/Variables/TenantVariables/ScopedTenantVariables/ScopedCommonTemplateScopeInlineCell";
import { TemplateNameCell } from "~/areas/projects/components/Variables/TenantVariables/TemplateNameCell";
import { buildScopedEnvironmentIdsByGroup } from "~/areas/projects/components/Variables/TenantVariables/buildScopedEnvironmentIdsByGroup";
import { getVariableTypeFromDisplaySettings } from "~/areas/projects/components/Variables/TenantVariables/getVariableTypeFromDisplaySettings";
import { pagingBarStyles, styles, tenantVariablesDataTable } from "~/areas/projects/components/Variables/TenantVariables/styles";
import { createCommonTemplatesKey, extractCommonTemplateIds } from "~/areas/projects/components/Variables/TenantVariables/useCommonTemplatesChangeTracker";
import type { CommonTemplatesChangeTracker, CommonTemplatesKey } from "~/areas/projects/components/Variables/TenantVariables/useCommonTemplatesChangeTracker";
import type { TenantVariableFiltersData } from "~/areas/projects/components/Variables/TenantVariables/useTenantVariableFiltersData";
import type { TenantVariableResourceOptions } from "~/areas/projects/components/Variables/TenantVariables/useTenantVariableResourceOptions";
import { TenantNameCellWithLink } from "~/areas/tenants/components/DataTable/Cells/TenantNameCell";
import NumberedPagingBar from "~/areas/tenants/components/Paging/NumberedPagingBar";
import { repository } from "~/clientInstance";
import { type DoBusyTask, useDoBusyTaskEffect } from "~/components/DataBaseComponent/index";
import { GroupedDataTable } from "~/components/GroupedDataTable/GroupedDataTable";
import { OverflowMenu, OverflowMenuItems } from "~/components/OverflowMenu/OverflowMenu";
import { isAllowed } from "~/components/PermissionCheck/PermissionCheck";
import SidebarLayout, { SidebarSide } from "~/components/SidebarLayout/index";
import { generateGuid } from "../../../Process/generation";
interface ScopedCommonTemplatesInlineTabContentProps {
    project: ProjectResource;
    connectedEnvironments: Map<string, EnvironmentResource[]>;
    doBusyTask: DoBusyTask;
    filterState: ProjectTenantVariablesFilterState;
    filterData: TenantVariableFiltersData;
    setFilterState: (filter: ProjectTenantVariablesFilterState) => void;
    pageSize: number;
    setPageState: (newPageNumber: number, newPageSize: number) => void;
    commonTemplatesWithValues: GetScopedCommonTemplatesWithValuesBffResponse;
    onAddTemplateFromOnboarding: (text: string) => void;
    resourceOptions: TenantVariableResourceOptions;
    changeTracker: CommonTemplatesChangeTracker;
}
export function ScopedCommonTemplatesInlineTabContent({ project, connectedEnvironments, doBusyTask, filterState, filterData, setFilterState, pageSize, setPageState, commonTemplatesWithValues, onAddTemplateFromOnboarding, resourceOptions, changeTracker, }: ScopedCommonTemplatesInlineTabContentProps) {
    const [variableSets, setVariableSets] = useState<LibraryVariableSetResource[] | null>(null);
    useDoBusyTaskEffect(doBusyTask, async () => {
        const libraryVariableSets = await repository.LibraryVariableSets.all({
            ids: project.IncludedLibraryVariableSetIds,
            contentType: VariableSetContentType.Variables,
        });
        setVariableSets(libraryVariableSets);
    }, [project]);
    if (variableSets === null) {
        return null;
    }
    if (variableSets.length === 0) {
        return <NoVariableSetsOnboarding project={project} doBusyTask={doBusyTask} onIncludeVariableSet={onAddTemplateFromOnboarding}/>;
    }
    const missingValueVariableSetIds = new Set<string>(commonTemplatesWithValues.MissingValueLibraryVariableSetIds);
    if (commonTemplatesWithValues.TotalValuesCount === 0) {
        return (<div style={{ height: "100%" }}>
                <SidebarLayout sideBar={<CommonTemplatesSidebarContent variableSets={variableSets} spaceId={project.SpaceId} pageSize={pageSize} filterState={filterState} setFilterState={setFilterState} missingValueVariableSetIds={missingValueVariableSetIds}/>} side={SidebarSide.Left} extendSidebarToEdges={true} extendContentToEdges={true} reduceWidthForSmallResolution={true}>
                    <NoCommonTemplateValuesOnboarding project={project}/>
                </SidebarLayout>
            </div>);
    }
    const [variableValues, bindableTemplateNames] = buildCommonTemplateRowData(commonTemplatesWithValues, variableSets, changeTracker);
    const unsavedChanges = changeTracker.values().length;
    return (<SidebarLayout sideBar={<CommonTemplatesSidebarContent variableSets={variableSets} spaceId={project.SpaceId} pageSize={pageSize} filterState={filterState} setFilterState={setFilterState} missingValueVariableSetIds={missingValueVariableSetIds}/>} side={SidebarSide.Left} extendSidebarToEdges={true} extendContentToEdges={true} reduceWidthForSmallResolution={true}>
            <div>
                {commonTemplatesWithValues.AnyMissingValues && <MissingValuesCallout filterState={filterState} setFilterState={setFilterState}/>}
                <ProjectTenantVariableTemplatesFiltersBar totalVariablesCount={commonTemplatesWithValues.TotalValuesCount} filteredVariablesCount={commonTemplatesWithValues.FilteredValuesCount} filterState={filterState} onFilterChange={setFilterState} filterData={filterData} unsavedChangesCount={unsavedChanges} doBusyTask={doBusyTask}/>
                <ScopedCommonTemplatesDataTable values={variableValues} project={project} accounts={commonTemplatesWithValues.Accounts} certificates={commonTemplatesWithValues.Certificates} workerPools={commonTemplatesWithValues.WorkerPools} connectedEnvironments={connectedEnvironments} resourceOptions={resourceOptions} changeTracker={changeTracker} doBusyTask={doBusyTask} bindableTemplateNames={bindableTemplateNames}/>
                <div className={pagingBarStyles}>
                    <NumberedPagingBar totalItems={commonTemplatesWithValues.FilteredValuesCount} pageNumber={filterState.pageNumber} pageSize={pageSize} pageSizeOptions={[30, 50, 100]} onPagingSelectionChange={setPageState}/>
                </div>
            </div>
        </SidebarLayout>);
}
interface ScopedCommonTemplatesDataTableProps {
    accounts: {
        [accountId: string]: string;
    };
    certificates: {
        [certificateId: string]: string;
    };
    workerPools: {
        [workerPoolId: string]: WorkerPoolResource;
    };
    connectedEnvironments: Map<string, EnvironmentResource[]>;
    project: ProjectResource;
    values: CommonTemplateRowData[];
    resourceOptions: TenantVariableResourceOptions;
    changeTracker: CommonTemplatesChangeTracker;
    doBusyTask: DoBusyTask;
    bindableTemplateNames: string[];
}
export function ScopedCommonTemplatesDataTable({ values, project, accounts, certificates, workerPools, connectedEnvironments, resourceOptions, changeTracker, doBusyTask, bindableTemplateNames }: ScopedCommonTemplatesDataTableProps) {
    const [focusedValueKey, setFocusedValueKey] = useState<CommonTemplatesKey | null>(null);
    const [focusedScopeKey, setFocusedScopeKey] = useState<CommonTemplatesKey | null>(null);
    const editingPermissions = {
        permission: Permission.VariableEdit,
        project: project.Id,
        wildcard: true,
    };
    const hasEditPermissions = isAllowed(editingPermissions);
    const groupsWithEmptyRow = new Set<string>();
    for (const row of values) {
        const trackedEntry = changeTracker.has(row.RowKey) ? changeTracker.get(row.RowKey) : undefined;
        const effectiveValue = trackedEntry ? trackedEntry.value : row.Variable.Value;
        if (!effectiveValue) {
            groupsWithEmptyRow.add(getGroupKey(row));
        }
    }
    const scopedEnvironmentIdsByGroup = buildScopedEnvironmentIdsByGroup(values.map((row) => {
        const tracked = changeTracker.has(row.RowKey) ? changeTracker.get(row.RowKey) : undefined;
        return {
            groupKey: getGroupKey(row),
            environmentIds: tracked ? tracked.environmentIds : row.Scope.EnvironmentIds,
        };
    }));
    const renderValueCell = (data: CommonTemplateRowData) => {
        const tenantId = data.Variable.TenantId;
        const sourceItems = resourceOptions.get(tenantId);
        const valueKey = data.RowKey;
        const trackedEntry = changeTracker.has(valueKey) ? changeTracker.get(valueKey) : undefined;
        const value = trackedEntry ? trackedEntry.value : data.Variable.Value;
        const hasValueChanged = changeTracker.has(valueKey);
        const handleOnBlur = () => {
            setFocusedValueKey((previous: CommonTemplatesKey | null) => (previous === valueKey ? null : previous));
        };
        return (<CommonTemplateValueInlineCell template={data.Template} accounts={accounts} certificates={certificates} workerPools={workerPools} value={value} valueType={data.VariableType} sourceItems={sourceItems} doBusyTask={doBusyTask} changeTracker={changeTracker} valueKey={valueKey} focused={focusedValueKey === valueKey} onFocus={() => setFocusedValueKey(valueKey)} onBlur={handleOnBlur} bindableTemplateNames={bindableTemplateNames} hasEditPermissions={hasEditPermissions} hasValueChanged={hasValueChanged} initialValue={data.Variable.Value} initialEnvironmentIds={data.Scope.EnvironmentIds}/>);
    };
    const renderScopeCell = (data: CommonTemplateRowData) => {
        const scopeKey = data.RowKey;
        const hasScopeChanged = changeTracker.has(scopeKey);
        const trackedEntry = changeTracker.has(scopeKey) ? changeTracker.get(scopeKey) : undefined;
        const value = trackedEntry ? trackedEntry.value : data.Variable.Value;
        const groupKey = getGroupKey(data);
        const groupEnvIds = scopedEnvironmentIdsByGroup.get(groupKey) ?? new Set<string>();
        const currentEnvIds = new Set(trackedEntry ? trackedEntry.environmentIds : data.Scope.EnvironmentIds);
        const availableEnvironments = (connectedEnvironments.get(data.Variable.TenantId) || []).filter((e) => !groupEnvIds.has(e.Id) || currentEnvIds.has(e.Id));
        const handleOnBlur = () => {
            setFocusedScopeKey((previous: CommonTemplatesKey | null) => (previous === scopeKey ? null : previous));
        };
        return (<ScopedCommonTemplateScopeInlineCell environments={availableEnvironments} changeTracker={changeTracker} scopeKey={scopeKey} focused={focusedScopeKey === scopeKey} onFocus={() => setFocusedScopeKey(scopeKey)} onBlur={handleOnBlur} hasEditPermissions={hasEditPermissions} canBeScoped={!!value} hasScopeChanged={hasScopeChanged} initialEnvironmentScope={data.Scope.EnvironmentIds} initialValue={data.Variable.Value}/>);
    };
    const groupByVariableSetName = (values: CommonTemplateRowData[]) => values.map((value) => ({
        key: value.VariableSet.Name,
        selector: (data: CommonTemplateRowData) => data.VariableSet.Name,
        header: (<tr style={{ backgroundColor: themeTokens.color.background.secondary.default }}>
                    <td colSpan={5} style={{ padding: `${space[8]} ${space[16]} ${space[8]} ${space[16]}` }}>
                        <div className={styles.variableSetHeader}>{value.VariableSet.Name}</div>
                    </td>
                </tr>),
    }));
    function onAddRow(data: CommonTemplateRowData) {
        const rowKey = createCommonTemplatesKey(data.VariableSetId, data.Template.Id, data.Variable.TenantId, generateGuid());
        changeTracker.add(rowKey, { value: undefined, environmentIds: [] });
    }
    function onDeleteRow(values: CommonTemplateRowData[], data: CommonTemplateRowData) {
        // If it's the last template value for a tenant, just empty it (keep the row visible)
        // Otherwise, remove the row completely
        const isLastTenantVariable = values.filter((v) => v.Template.Id === data.Template.Id && v.Variable.TenantId === data.Variable.TenantId).length === 1;
        if (isLastTenantVariable) {
            // Check if this is a new row (just added) or an existing row
            const trackedEntry = changeTracker.has(data.RowKey) ? changeTracker.get(data.RowKey) : undefined;
            const isNewRow = trackedEntry?.status === "new";
            if (isNewRow) {
                // For new rows that are the last one, just remove them completely
                changeTracker.remove(data.RowKey);
            }
            else {
                // For existing rows that are the last one, keep the row but clear value and scope
                changeTracker.set(data.RowKey, { value: undefined, environmentIds: [] });
            }
            return;
        }
        // Remove the row completely by marking as deleted
        changeTracker.delete(data.RowKey);
    }
    return (<GroupedDataTable data={values} tableClassName={tenantVariablesDataTable} groupBy={groupByVariableSetName(values)} columns={[
            { title: "Name", render: (data) => (data.IsTemplateChild ? null : <TemplateNameCell template={data.Template}/>), width: "15%", isChild: (data) => data.IsTemplateChild },
            {
                title: "Tenant",
                isChild: (data) => data.IsTenantChild,
                render: (data) => data.IsTenantChild ? null : (<TenantNameCellWithLink spaceId={project.SpaceId} tenantId={data.Variable.TenantId} tenantName={data.Variable.TenantName} tenantLogoLink={data.Variable.TenantLogoLink} isDisabled={data.Variable.TenantIsDisabled} truncate={data.Variable.TenantName.length > 20} linkToPage="Variables"/>),
                width: "20%",
            },
            {
                title: "Value",
                render: renderValueCell,
                width: "15%",
                padding: "0",
                backgroundColor: (data) => {
                    const hasValueChanged = changeTracker.hasValueChanged(data.RowKey, data.Variable.Value);
                    return hasValueChanged ? themeTokens.color.callout.background.warning.default : undefined;
                },
            },
            {
                title: "Scope",
                width: "35%",
                render: renderScopeCell,
                backgroundColor: (data) => {
                    const hasScopeChanged = changeTracker.hasScopeChanged(data.RowKey, data.Scope.EnvironmentIds);
                    return hasScopeChanged ? themeTokens.color.callout.background.warning.default : undefined;
                },
            },
            {
                title: "",
                render: (data) => {
                    const groupKey = getGroupKey(data);
                    const canAdd = !groupsWithEmptyRow.has(groupKey);
                    return (<div className={styles.variablesOverflowMenu}>
                                {hasEditPermissions ? (<OverflowMenu accessibleName="variableOverflowMenu" menuItems={[
                                canAdd ? OverflowMenuItems.item("Add", () => onAddRow(data)) : OverflowMenuItems.disabledItem("Add", "Fill in the current row before adding another"),
                                OverflowMenuItems.item("Delete", () => {
                                    onDeleteRow(values, data);
                                }),
                            ]}/>) : null}
                            </div>);
                },
                width: "5%",
            },
        ]} getRowKey={(data) => data.RowKey}/>);
}
interface Scope {
    EnvironmentIds: string[];
}
interface CommonTemplateRowData {
    VariableSetId: string;
    VariableSet: LibraryVariableSetInfo;
    Variable: ScopedCommonTemplateTenantValue;
    Template: ActionTemplateParameterResource;
    VariableType: VariableType;
    RowKey: CommonTemplatesKey;
    Scope: Scope;
    IsTemplateChild: boolean;
    IsTenantChild: boolean;
}
function getGroupKey(item: CommonTemplateRowData): string {
    return `${item.VariableSetId}:${item.Template.Id}:${item.Variable.TenantId}`;
}
function buildCommonTemplateRowData(commonTemplatesWithValues: GetScopedCommonTemplatesWithValuesBffResponse, variableSets: LibraryVariableSetResource[], changeTracker: CommonTemplatesChangeTracker): [
    CommonTemplateRowData[],
    string[]
] {
    const rows: CommonTemplateRowData[] = [];
    const [templatesLookup, bindableNames] = buildTemplatesLookup(variableSets);
    const templatesWithChildren = new Set<string>();
    // Track removed variables (those marked for deletion)
    const removedVariables = new Set<CommonTemplatesKey>();
    for (const [key] of changeTracker.removedVariables()) {
        removedVariables.add(key);
    }
    for (const variableSet of commonTemplatesWithValues.VariableSets) {
        const set = variableSet.LibraryVariableSet;
        for (const templateWithValues of variableSet.Templates) {
            const tenantsWithChildren = new Set<string>();
            const tenantVariablesMap = new Map<string, ScopedCommonTemplateTenantValue[]>();
            for (let i = 0; i < templateWithValues.Values.length; i++) {
                const variable = templateWithValues.Values[i];
                const tenantId = variable.TenantId;
                const existing = tenantVariablesMap.get(tenantId);
                if (existing) {
                    existing.push(variable);
                }
                else {
                    tenantVariablesMap.set(tenantId, []);
                    tenantVariablesMap.get(tenantId)?.push(variable);
                }
            }
            for (const [tenantId, variables] of tenantVariablesMap) {
                const variable = variables[variables.length - 1];
                const newVariablesMap = changeTracker.newVariables(set.Id, variable.TemplateId, variable.TenantId);
                for (const [key, entry] of newVariablesMap) {
                    const { guid } = extractCommonTemplateIds(key);
                    const newVariable = {
                        ...variable,
                        Id: guid,
                        Value: entry.value,
                        Scope: { EnvironmentIds: entry.environmentIds },
                    };
                    tenantVariablesMap.get(tenantId)?.push(newVariable);
                }
                const variablesToKeep = variables.filter((v) => {
                    const key = createCommonTemplatesKey(set.Id, variable.TemplateId, variable.TenantId, v.Id);
                    return !removedVariables.has(key);
                });
                tenantVariablesMap.set(tenantId, variablesToKeep);
            }
            for (const [tenantId, variables] of tenantVariablesMap) {
                for (const variable of variables.values()) {
                    const templateWithType = templatesLookup.get(variable.TemplateId);
                    if (!templateWithType) {
                        logger.warn("Could not find common template with Id {id}", { id: variable.TemplateId });
                        continue;
                    }
                    const { template, type } = templateWithType;
                    const isTemplateHasChildren = templatesWithChildren.has(template.Id);
                    if (!isTemplateHasChildren) {
                        templatesWithChildren.add(template.Id);
                    }
                    const isTenantHasChildren = tenantsWithChildren.has(variable.TenantId);
                    if (!isTenantHasChildren) {
                        tenantsWithChildren.add(variable.TenantId);
                    }
                    const row: CommonTemplateRowData = {
                        VariableSetId: set.Id,
                        VariableSet: set,
                        Variable: variable,
                        Template: template,
                        VariableType: type,
                        RowKey: createCommonTemplatesKey(set.Id, template.Id, variable.TenantId, variable.Id),
                        Scope: { EnvironmentIds: variable.Scope.EnvironmentIds },
                        IsTemplateChild: isTemplateHasChildren,
                        IsTenantChild: isTenantHasChildren,
                    };
                    rows.push(row);
                }
            }
        }
    }
    return [rows, bindableNames];
}
interface TemplateWithVariableType {
    template: ActionTemplateParameterResource;
    type: VariableType;
}
function buildTemplatesLookup(variableSets: LibraryVariableSetResource[]): [
    Map<string, TemplateWithVariableType>,
    string[]
] {
    const templates = new Map<string, TemplateWithVariableType>();
    const bindableNames = new Set<string>();
    for (const variableSet of variableSets) {
        for (const template of variableSet.Templates) {
            const type = getVariableTypeFromDisplaySettings(template.DisplaySettings["Octopus.ControlType"]);
            templates.set(template.Id, { template, type });
            bindableNames.add(template.Name);
        }
    }
    return [templates, Array.from(bindableNames)];
}
